import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { useEventStore } from "store";
import { FormattedMessage } from "react-intl";
import teamMatchFrame from "../assets/images/team-match-frame.svg";
import photoChallengeFrame from "../assets/images/photo-challenge-frame.svg";
import quizFrame from "../assets/images/quiz-frame.svg";
import bingoFrame from "../assets/images/bingo-frame.svg";
import postFrame from "../assets/images/post-frame.svg";

type Props = {
  file: File | null;
  onFinish: (file: File) => void;
  onCancel: () => void;
  maxZoom?: number;
  frameStyle?: "bingo" | "photo-challenge" | "team-match" | "quiz";
  frameSelector?: boolean;
};

type ImgCroppedArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export default function ImageCropper({
  file,
  onFinish,
  onCancel,
  maxZoom,
  frameStyle,
  frameSelector,
}: Props) {
  const { theme } = useEventStore();
  const [image, setImage] = useState<any>(null);
  const [imageNaturalSize, setImageNaturalSize] = useState({
    width: 0,
    height: 0,
  });
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [useFrame, setUseFrame] = useState(true);
  const [croppedArea, setCroppedArea] = useState<ImgCroppedArea>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  // Map frame styles to their respective SVG files
  const getFramePath = (style: string) => {
    switch (style) {
      case "team-match":
        return teamMatchFrame;
      case "bingo":
        return bingoFrame;
      case "photo-challenge":
        return photoChallengeFrame;
      case "quiz":
        return quizFrame;
      default:
        return postFrame;
    }
  };

  const frameStylePath = frameStyle ? getFramePath(frameStyle) : postFrame;

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedArea(croppedAreaPixels);
  };

  const onZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setZoom(parseFloat(e.target.value));
  };

  // Calculate canvas dimensions based on image and frame usage
  const getCanvasDimensions = useCallback(() => {
    if (useFrame) {
      // With frame: always 960x960
      return {
        width: 1020,
        height: 1190,
        cropWidth: 960,
        cropHeight: 960,
        cropX: 30,
        cropY: 30,
      };
    } else {
      // Without frame: based on image's smaller dimension being 960
      const { width: naturalWidth, height: naturalHeight } = imageNaturalSize;

      if (naturalWidth === 0 || naturalHeight === 0) {
        // Fallback if image dimensions not available
        return {
          width: 960,
          height: 960,
          cropWidth: 960,
          cropHeight: 960,
          cropX: 0,
          cropY: 0,
        };
      }

      const isPortrait = naturalHeight > naturalWidth;

      if (isPortrait) {
        // Portrait: width = 960, height maintains aspect ratio
        const aspectRatio = naturalHeight / naturalWidth;
        const cropHeight = Math.round(960 * aspectRatio);
        return {
          width: 960,
          height: cropHeight,
          cropWidth: 960,
          cropHeight,
          cropX: 0,
          cropY: 0,
        };
      } else {
        // Landscape: height = 960, width maintains aspect ratio
        const aspectRatio = naturalWidth / naturalHeight;
        const cropWidth = Math.round(960 * aspectRatio);
        return {
          width: cropWidth,
          height: 960,
          cropWidth,
          cropHeight: 960,
          cropX: 0,
          cropY: 0,
        };
      }
    }
  }, [useFrame, imageNaturalSize]);

  // Calculate aspect ratio for cropper
  const getCropperAspectRatio = () => {
    if (useFrame) {
      return 960 / 960; // Frame aspect ratio
    } else {
      const { width: naturalWidth, height: naturalHeight } = imageNaturalSize;

      if (naturalWidth === 0 || naturalHeight === 0) {
        return 1; // Square fallback
      }

      // Use the original aspect ratio of the image
      return naturalWidth / naturalHeight;
    }
  };

  const confirmCropHandler = useCallback(() => {
    const canvasDimensions = getCanvasDimensions();
    const canvasEle = document.createElement("canvas");
    canvasEle.width = canvasDimensions.width;
    canvasEle.height = canvasDimensions.height;

    const context = canvasEle.getContext("2d");
    const imageCropped = new Image();
    imageCropped.src = image;

    imageCropped.onload = function () {
      context?.drawImage(
        imageCropped,
        croppedArea.x,
        croppedArea.y,
        croppedArea.width,
        croppedArea.height,
        canvasDimensions.cropX,
        canvasDimensions.cropY,
        canvasDimensions.cropWidth,
        canvasDimensions.cropHeight
      );

      const createFinalImage = () => {
        const originalFileName = file?.name || "post-img.jpg";
        const fileExtension =
          originalFileName.split(".").pop()?.toLowerCase() || "jpg";
        const fileType = file?.type || "image/jpg";
        const outputQuality = 0.8;
        const croppedFileName = `img-cropped.${fileExtension}`;

        canvasEle.toBlob(
          async (blob) => {
            if (!blob) {
              return;
            }
            const croppedFile = new File([blob], croppedFileName, {
              type: fileType,
              lastModified: Date.now(),
            });

            onFinish(croppedFile);
            setImage(null);
          },
          fileType,
          outputQuality
        );
      };

      if (useFrame) {
        const frameImage = new Image();
        frameImage.src = frameStylePath;

        frameImage.onload = function () {
          context?.drawImage(
            frameImage,
            0,
            0,
            canvasDimensions.width,
            canvasDimensions.height
          );
          createFinalImage();
        };

        frameImage.onerror = function () {
          console.error("Error loading frame image.");
          createFinalImage();
        };
      } else {
        createFinalImage();
      }
    };
  }, [
    croppedArea,
    image,
    onFinish,
    file,
    useFrame,
    getCanvasDimensions,
    frameStylePath,
  ]);

  useEffect(() => {
    if (file) {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setImage(fileReader.result);

        // Get natural dimensions of the image
        const img = new Image();
        img.onload = () => {
          setImageNaturalSize({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        };
        img.src = fileReader.result as string;
      };
      fileReader.readAsDataURL(file);
    }
  }, [file]);

  const toggleFrame = () => {
    setUseFrame(!useFrame);
    // Reset crop and zoom when toggling frame
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const getContainerHeight = () => {
    if (useFrame && frameSelector) {
      return "h-[550px]";
    } else if (useFrame) {
      return "h-[510px]";
    } else {
      return "h-[490px]";
    }
  };

  return (
    <div className={`relative w-full ${getContainerHeight()}`}>
      <div className="absolute w-[350px] h-full bg-transparent left-1/2 -translate-x-1/2">
        <Cropper
          image={image}
          aspect={getCropperAspectRatio()}
          crop={crop}
          zoom={zoom}
          maxZoom={maxZoom || 2}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          showGrid={true}
          objectFit={useFrame ? "cover" : "contain"}
          style={{
            containerStyle: {
              backgroundColor: `black`,
              height: `350px`,
              width: `330px`,
              minWidth: `330px`,
              minHeight: `350px`,
              maxWidth: `330px`,
              maxHeight: `350px`,
            },
            cropAreaStyle: {},
            mediaStyle: {},
          }}
          classes={{
            containerClassName: "mx-auto",
          }}
        />
        {useFrame && (
          <div
            className="absolute inset-0 pointer-events-none flex items-center justify-center"
            style={{ height: `408px`, width: `350px`, color: `#000` }}
          >
            <div className="relative h-full w-full shadow-lg shadow-gray-400">
              <img
                src={frameStylePath}
                alt="Frame overlay"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-0 w-full">
        <div className="flex flex-col items-start justify-center">
          {frameSelector && (
            <div className="bg-white bg-opacity-70 my-2 px-2 py-1 rounded-md">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useFrame}
                  onChange={toggleFrame}
                  className="cursor-pointer w-5 h-5 appearance-none border-2 border-gray-300 rounded checked:bg-primary-rose checked:border-primary-rose checked:bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEzLjMzMzMgNEw2IDEyTDIuNjY2NjcgOC42NjY2NyIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+')] checked:bg-no-repeat checked:bg-center checked:bg-[length:12px_12px]"
                />
                <span>
                  <FormattedMessage
                    id="cropper.use.frame"
                    defaultMessage="Usar Moldura"
                  />
                </span>
              </label>
            </div>
          )}
          <input
            type="range"
            className={`p-0 mb-3 mt-2 w-full h-2 ${
              theme.name === "rose" ? "bg-rose-200" : "bg-gray-200"
            } rounded-lg appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-5
              [&::-webkit-slider-thumb]:h-5
              [&::-webkit-slider-thumb]:rounded-full
              ${
                theme.name === "rose"
                  ? "[&::-webkit-slider-thumb]:bg-primary-rose"
                  : "[&::-webkit-slider-thumb]:bg-gray-600"
              }`}
            min={1}
            max={maxZoom || 2}
            step={0.001}
            value={zoom}
            onChange={onZoomChange}
          />

          <div className="mt-3 w-full flex items-center justify-around">
            <button
              onClick={onCancel}
              className={`${
                theme.name === "rose" ? theme.buttonSecondary : theme.button
              } shadow-md shadow-gray-300 font-century-gothic-bold text-sm rounded-[10px] text-theme-black h-min px-10 x flex justify-center items-center`}
            >
              <FormattedMessage
                id="feed.button.options.cancel"
                defaultMessage="Cancelar"
              />
            </button>
            <button
              onClick={confirmCropHandler}
              className={`${
                theme.name === "rose" ? theme.button : theme.buttonSecondary
              }  shadow-md shadow-gray-300 font-century-gothic-bold text-sm rounded-[10px] text-theme-black h-min px-10 x flex justify-center items-center`}
            >
              <FormattedMessage
                id="feed.button.options.confirm"
                defaultMessage="Confirmar"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
